import React, { useState, useEffect, useRef, useCallback } from "react";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import Canvas from "./Canva";
import LeftSidebar from "./LeftSidebar";
import RightSidebar from "./RightSidebar";
import { ThemeProvider, createTheme } from "@mui/material/styles";
import {
  Box,
  Button,
  TextField,
  Stack,
  Paper,
  CircularProgress,
} from "@mui/material";
import DownloadIcon from "@mui/icons-material/Download";
import SaveIcon from "@mui/icons-material/Save";
import { useNavigate, useParams } from "react-router-dom";
import { useSelector } from "react-redux";
import TruInkUpdateTagModel from "../Components/TruInkUpdateTagModel";
import callPiggybackAPI from "../Helpers/piggyback.api.helper";
import {
  saveDesign,
  loadInitialData,
} from "../../../utils/templateComponentServices";
import {
  calculateCanvasDimensions,
  downloadGenerateCanvasImage,
} from "../../../utils/canvaUtils";

const theme = createTheme({
  palette: {
    primary: { main: "#1976d2" },
    background: { default: "#f0f2f5", paper: "#ffffff" },
    customGray: { main: "#4a4a4a" },
  },
});

export default function App() {
  const canvasRef = useRef();
  const rightSideRef = useRef();
  const { templateId } = useParams();
  const selectedSite = useSelector((state) => state.truInk.selectedSite);
  const siteId = selectedSite?.id;
  const tenantId = selectedSite?.tenantId;
  const navigate = useNavigate();

  const [elements, setElements] = useState([]);
  const [selectedElement, setSelectedElement] = useState(null);
  const [history, setHistory] = useState([]);
  const [redoStack, setRedoStack] = useState([]);
  const [clipboard, setClipboard] = useState(null);
  const [eventName, setEventName] = useState("Template");
  const [resolution, setResolution] = useState("1920x1080");
  const [isSaving, setIsSaving] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [showBulkUpdateModel, setShowBulkUpdateModel] = useState(false);
  const [canvasColor, setCanvasColor] = useState([]);
  const [dataBindingValue, setDataBindingValue] = useState([]);
  const [internalConfigs, setInternalConfigs] = useState([]);
  const [truInkEMMSTemplate, setTruInkEMMSTemplate] = useState(false);
  const [templateMode, setTemplateMode] = useState("landscape");
  const [showIcons, setShowIcons] = useState(false);
  useEffect(() => {
    if (!siteId || !tenantId) return;
    callPiggybackAPI(
      "pluginSettings",
      { siteId, tenantId },
      {},
      "GET"
    )
      .then((res) => setShowIcons(Array.isArray(res) && res.length > 0))
      .catch(() => setShowIcons(false));
  }, [siteId, tenantId]);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (!canvasRef.current || !rightSideRef.current) return;
      if (
        e.target.closest(".MuiPopover-root") ||
        e.target.closest(".MuiMenu-root") ||
        e.target.closest(".MuiModal-root")
      ) {
        return;
      }
      if (
        !canvasRef.current.contains(e.target) &&
        !rightSideRef.current.contains(e.target)
      ) {
        setSelectedElement(null);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [canvasRef, rightSideRef]);

  const canvasDimensions = useCallback(
    () => calculateCanvasDimensions(resolution),
    [resolution],
  );

  // Helper function to swap resolution dimensions
  const swapResolutionDimensions = (res) => {
    const [width, height] = res.split("x").map(Number);
    return `${height}x${width}`;
  };

  const applyDataBinding = useCallback((bindingData) => {
    if (!bindingData || !Array.isArray(bindingData)) return;

    setElements((prevElements) => {
      const updatedElements = prevElements.map((element) => {
        if (element.attribute_binding?.data_key) {
          const dataKey = element.attribute_binding.data_key;

          let newValue = null;
          // First, try to find in bindingData
          for (const item of bindingData) {
            if (item.fields && item.fields[dataKey] !== undefined) {
              newValue = String(item.fields[dataKey]);
              break;
            }
          }

          // If not found in bindingData, check internal configs
          if (newValue === null) {
            // Check if dataKey is in format "configName.key" (e.g., "dynamicConfig.tagmac")
            const configKeyMatch = dataKey?.match(/^(.+)\.(.+)$/);
            if (configKeyMatch) {
              const [, configName, key] = configKeyMatch;
              const isInternal = internalConfigs.find(
                (c) => c.InternalConfigurationName === configName
              );
              if (isInternal?.InternalConfigurationValue?.[key] !== undefined) {
                newValue = String(isInternal.InternalConfigurationValue[key]);
              }
            } else {
              // Fallback: check if dataKey matches InternalConfigurationName directly
              const isInternal = internalConfigs.find(
                (c) => c.InternalConfigurationName === dataKey
              );
              if (isInternal) {
                newValue = `{${isInternal.InternalConfigurationName}}`;
              }
            }
          }

          // Apply data binding if we found a value
          if (newValue !== null) {
            const originalContent = element.originalContent || element.content;
            const originalFontSize =
              element.originalFontSize || element.fontSize;

            const calculateOptimalFontSize = (
              text,
              maxWidth,
              maxHeight,
              baseSize,
              fontFamily,
            ) => {
              const canvas = document.createElement("canvas");
              const context = canvas.getContext("2d");

              const fontFamilyToUse = fontFamily || "Arial";
              context.font = `${baseSize}px "${fontFamilyToUse}", Arial, sans-serif`;

              const metrics = context.measureText(text);

              if (metrics.width <= maxWidth && baseSize <= maxHeight) {
                return baseSize;
              }

              const widthScale = maxWidth / metrics.width;
              const heightScale = maxHeight / baseSize;
              const scaleFactor = Math.min(widthScale, heightScale);

              const optimalSize = Math.floor(baseSize * scaleFactor * 0.95);

              return Math.max(6, Math.min(120, optimalSize));
            };

            const containerWidth = element.width - 10;
            const containerHeight = element.height - 10;

            const calculatedFontSize = calculateOptimalFontSize(
              newValue,
              containerWidth,
              containerHeight,
              originalFontSize,
              element.fontFamily,
            );

            return {
              ...element,
              content: originalContent,
              fontSize: originalFontSize,
              dataBoundContent: newValue,
              dataBoundFontSize: calculatedFontSize,
              originalContent: originalContent,
              originalFontSize: originalFontSize,
              isDataBound: true,
            };
          }
        }
        return element;
      });
      const dataBoundElements = updatedElements.filter((el) => el.isDataBound);

      return updatedElements;
    });
  }, []);

  useEffect(() => {
    const dataBoundElements = elements.filter((el) => el.isDataBound);
    if (dataBoundElements.length > 0) {
      console.log("Current data-bound elements in state:", dataBoundElements);
    }
  }, [elements]);

  useEffect(() => {
    if (templateId && tenantId) {
      const fetchTemplateData = async () => {
        setIsLoading(true);
        try {
          const {
            elements,
            eventName,
            resolution,
            canvasColor,
            dataBindingValues,
            internalConfigs,
            truInkEMMSTemplate,
            customProperties,
          } = await loadInitialData(templateId, tenantId, siteId);

          const processedElements = elements.map((element) => {
            if (element.type === "text") {
              return {
                ...element,
                content: element.originalContent || element.content,
                fontSize: element.originalFontSize || element.fontSize,
                originalContent: element.originalContent || element.content,
                originalFontSize: element.originalFontSize || element.fontSize,
                dataBoundContent: element.dataBoundContent,
                dataBoundFontSize: element.dataBoundFontSize,
                isDataBound: element.isDataBound || false,
                rotation: element.rotation || 0,
                group: element.group,
                groupLocked: element.groupLocked,
                groupVisible: element.groupVisible,
                rotation: element.rotation || 0,
                verticalAlign: element.verticalAlign,
              };
            }
            if (element.type === "image") {
              return {
                ...element,
                ditheringEnabled: element.ditheringEnabled !== false,
                rotation: element.rotation || 0,
                group: element.group,
                groupLocked: element.groupLocked,
                groupVisible: element.groupVisible,
                rotation: element.rotation || 0,
              };
            }
            return {
              ...element,
              rotation: element.rotation || 0,
              group: element.group,
              groupLocked: element.groupLocked,
              groupVisible: element.groupVisible,
              rotation: element.rotation || 0,
            };
          });

          setElements(processedElements);
          setEventName(eventName);

          // Set template mode and adjust resolution if portrait
          const mode = customProperties?.mode || "landscape";
          setTemplateMode(mode);
          const adjustedResolution =
            mode === "portrait"
              ? swapResolutionDimensions(resolution)
              : resolution;
          setResolution(adjustedResolution);

          setCanvasColor(canvasColor);
          setDataBindingValue(dataBindingValues);
          setInternalConfigs(internalConfigs);
          setTruInkEMMSTemplate(!!truInkEMMSTemplate);
          setTimeout(() => {
            if (dataBindingValues && dataBindingValues.length > 0) {
              applyDataBinding(dataBindingValues);
            }
          }, 100);

          toast.success("Template loaded successfully!");
        } catch (error) {
          console.error("Error loading template:", error);
          toast.error(`Error loading template: ${error.message}`);
        } finally {
          setIsLoading(false);
        }
      };
      fetchTemplateData();
    }
  }, [templateId, tenantId, siteId, applyDataBinding]);

  const handleSaveDesign = async () => {
    if (!tenantId || !siteId) {
      toast.error("Tenant ID and Site ID are required to save the design");
      return;
    }

    setIsSaving(true);
    try {
      const processedElements = elements.map((element) => {
        const processed = JSON.parse(JSON.stringify(element));
        if (element.type === "text") {
          processed.dataBoundContent = element.dataBoundContent;
          processed.dataBoundFontSize = element.dataBoundFontSize;
          processed.originalContent = element.originalContent;
          processed.originalFontSize = element.originalFontSize;
          processed.isDataBound = element.isDataBound;
        }
        if (element.type === "image") {
          processed.ditheringEnabled = element.ditheringEnabled !== false;
        }
        if (element.type === "qr") {
          const qrUrl = `https://api.qrserver.com/v1/create-qr-code/?size=${
            element.width
          }x${element.height}&data=${
            element.url
          }&color=${element.fill.substring(1)}`;
          processed.imgUrl = qrUrl;
        }
        processed.rotation =
          element.rotation !== undefined ? element.rotation : 0;
        processed.group =
          element.group !== undefined ? element.group : undefined;
        processed.groupLocked =
          element.groupLocked !== undefined ? element.groupLocked : undefined;
        processed.groupVisible =
          element.groupVisible !== undefined ? element.groupVisible : undefined;

        return processed;
      });

      const response = await saveDesign(
        processedElements,
        eventName,
        tenantId,
        siteId,
        canvasDimensions(),
        templateId,
        navigate,
      );

      if (templateId) {
        setShowBulkUpdateModel(true);
        toast.success("Template updated successfully!");
      } else {
        toast.success("Template saved successfully!");
        if (response.id) {
          navigate(`/${response.id}`);
        }
      }
    } catch (error) {
      const errorMessage =
        error.response?.data?.message || error.message || "Failed to save";
      toast.error(`Error saving template: ${errorMessage}`);
    } finally {
      setIsSaving(false);
    }
  };

  const handleDownloadClick = async () => {
    const selectedId = selectedElement;
    setSelectedElement(null);
    await new Promise((resolve) => setTimeout(resolve, 50));
    const dataUrl = await downloadGenerateCanvasImage(canvasRef, resolution);
    if (dataUrl) {
      const link = document.createElement("a");
      link.download = `${eventName || "design"}.png`;
      link.href = dataUrl;
      link.click();
    }
    setSelectedElement(selectedId);
  };
  const pushToHistory = useCallback(
    (newElements) => {
      setHistory((prev) => [...prev, elements]);
      setRedoStack([]);
      setElements(newElements);
    },
    [elements],
  );

  const undo = useCallback(() => {
    if (history.length === 0) return;
    const prev = history[history.length - 1];
    setHistory(history.slice(0, -1));
    setRedoStack((prevRedo) => [elements, ...prevRedo]);
    setElements(prev);
    setSelectedElement(null);
  }, [history, elements]);

  const redo = useCallback(() => {
    if (redoStack.length === 0) return;
    const next = redoStack[0];
    setRedoStack(redoStack.slice(1));
    setHistory((prev) => [...prev, elements]);
    setElements(next);
    setSelectedElement(null);
  }, [redoStack, elements]);

  const updateElement = useCallback(
    (id, updates, pushHistory = true) => {
      setElements((prevElements) => {
        const newElements = prevElements.map((el) => {
          if (el.id === id) {
            let updatedElement = { ...el, ...updates };
            if (el.type === "text") {
              if (updates.content !== undefined) {
                updatedElement.originalContent = updates.content;
                if (el.isDataBound && updates.content !== el.originalContent) {
                  updatedElement.isDataBound = false;
                  updatedElement.dataBoundContent = null;
                  updatedElement.dataBoundFontSize = null;
                }
              }
              if (updates.fontSize !== undefined) {
                updatedElement.originalFontSize = updates.fontSize;
                if (el.isDataBound) {
                  updatedElement.dataBoundFontSize = updates.fontSize;
                }
              }
            }
            return updatedElement;
          }
          return el;
        });

        // Only push to history if flag is true
        if (
          pushHistory &&
          JSON.stringify(newElements) !== JSON.stringify(prevElements)
        ) {
          pushToHistory(newElements);
        }
        return newElements;
      });
    },
    [pushToHistory],
  );

  useEffect(() => {
    if (dataBindingValue && dataBindingValue.length > 0) {
      applyDataBinding(dataBindingValue);
    }
  }, [dataBindingValue, applyDataBinding]);

  const addElement = useCallback(
    (type) => {
      const { width, height } = canvasDimensions();
      const defaultWidth = Math.max(100, Math.floor(width / 2));
      const defaultHeight = Math.max(100, Math.floor(height / 2));
      const x = Math.max(0, (width - defaultWidth) / 2);
      const y = Math.max(0, (height - defaultHeight) / 2);

      const newElement = {
        id: Date.now(),
        type,
        x,
        y,
        width: defaultWidth,
        height: defaultHeight,
        rotation: 0,
        group: "",
        ...(type === "text" && {
          content: "New Text",
          fontSize: 16,
          fill: "#000000",
          originalContent: "New Text",
          originalFontSize: 16,
        }),
        ...(type === "image" && { src: "", ditheringEnabled: true }),
        ...(type === "shape" && { shapeType: "rectangle", fill: "#FF0000" }),
        ...(type === "qr" && { url: "https://example.com", fill: "#000000" }),
        ...(type === "line" && { direction: "horizontal", fill: "#000000" }),
        ...(type === "labels" && { direction: "row", alignment: "left", ditheringEnabled: false }),
      };
      pushToHistory([...elements, newElement]);
      setSelectedElement(newElement.id);
    },
    [elements, pushToHistory, canvasDimensions],
  );

  const deleteElement = useCallback(
    (id) => {
      const newElements = elements.filter((el) => el.id !== id);
      pushToHistory(newElements);
      setSelectedElement(null);
    },
    [elements, pushToHistory],
  );

  const duplicateElement = useCallback(
    (id) => {
      const element = elements.find((el) => el.id === id);
      if (!element) return;
      const { width, height } = canvasDimensions();
      const duplicated = {
        ...element,
        id: Date.now(),
        x: Math.min(element.x + 20, width - element.width),
        y: Math.min(element.y + 20, height - element.height),
        rotation: element.rotation || 0,
      };
      pushToHistory([...elements, duplicated]);
      setSelectedElement(duplicated.id);
    },
    [elements, pushToHistory, canvasDimensions],
  );

  const bringForward = useCallback(
    (id) => {
      const index = elements.findIndex((el) => el.id === id);
      if (index < elements.length - 1) {
        const newElements = [...elements];
        const [item] = newElements.splice(index, 1);
        newElements.splice(index + 1, 0, item);
        pushToHistory(newElements);
      }
    },
    [elements, pushToHistory],
  );

  const sendBackward = useCallback(
    (id) => {
      const index = elements.findIndex((el) => el.id === id);
      if (index > 0) {
        const newElements = [...elements];
        const [item] = newElements.splice(index, 1);
        newElements.splice(index - 1, 0, item);
        pushToHistory(newElements);
      }
    },
    [elements, pushToHistory],
  );

  useEffect(() => {
    const handleKeyDown = (e) => {
      const isMac = navigator.platform.includes("Mac");
      const ctrlKey = isMac ? e.metaKey : e.ctrlKey;
      if (ctrlKey && e.key === "z" && !e.shiftKey) {
        e.preventDefault();
        undo();
      }
      if (ctrlKey && e.key === "z" && e.shiftKey) {
        e.preventDefault();
        redo();
      }
      if (ctrlKey && e.key === "d" && selectedElement) {
        e.preventDefault();
        duplicateElement(selectedElement);
      }
      if (e.key === "Escape") {
        e.preventDefault();
        setSelectedElement(null);
      }
      if (ctrlKey && e.key === "c" && selectedElement) {
        e.preventDefault();
        const el = elements.find((el) => el.id === selectedElement);
        if (el) {
          setClipboard({ ...el });
        }
      }
      if (e.key === "Delete" && selectedElement) {
        e.preventDefault();
        deleteElement(selectedElement);
      }

      if (ctrlKey && e.key === "v" && clipboard) {
        e.preventDefault();
        const { width, height } = canvasDimensions();
        const newElement = {
          ...clipboard,
          id: Date.now(),
          x: Math.min(clipboard.x + 20, width - clipboard.width),
          y: Math.min(clipboard.y + 20, height - clipboard.height),
          rotation: clipboard.rotation || 0,
        };
        pushToHistory([...elements, newElement]);
        setSelectedElement(newElement.id);
      }
      if (e.key === "Tab" && elements.length > 0) {
        e.preventDefault();

        const currentIndex = elements.findIndex(
          (el) => el.id === selectedElement,
        );

        let nextIndex;

        if (e.shiftKey) {
          // Shift + Tab → previous
          nextIndex =
            currentIndex <= 0 ? elements.length - 1 : currentIndex - 1;
        } else {
          // Tab → next
          nextIndex =
            currentIndex === -1 || currentIndex === elements.length - 1
              ? 0
              : currentIndex + 1;
        }

        setSelectedElement(elements[nextIndex].id);
      }
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [
    selectedElement,
    elements,
    clipboard,
    undo,
    redo,
    duplicateElement,
    pushToHistory,
    canvasDimensions,
  ]);

  return (
    <ThemeProvider theme={theme}>
      <Box
        sx={{ display: "flex", height: "100vh", bgcolor: "background.default" }}
      >
        {/* Left Sidebar */}
        <LeftSidebar onAddElement={addElement} templateMode={templateMode} showIcons={showIcons} />
        {/* Main Content Area */}
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            flexGrow: 1,
            overflow: "hidden",
          }}
        >
          <Paper
            elevation={3}
            sx={{
              p: 2,
              bgcolor: "white",
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              flexWrap: "wrap",
              gap: 2,
            }}
          >
            <Stack direction="row" spacing={0.5} alignItems="center">
              <TextField
                variant="standard"
                value={eventName}
                InputProps={{
                  readOnly: true,
                  disableUnderline: true,
                  sx: {
                    maxWidth: "120px",
                  },
                }}
                inputProps={{
                  style: {
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                    fontSize: "1.2rem",
                    fontWeight: "bold",
                    color: "black",
                  },
                }}
                title={eventName}
              />
              <div
                style={{
                  backgroundColor:
                    templateMode === "portrait" ? "#ff6b6b" : "#4ecdc4",
                  color: "white",
                  padding: "5px 10px",
                  borderRadius: "4px",
                  fontSize: "8px",
                  fontWeight: "bold",
                  textTransform: "uppercase",
                  whiteSpace: "nowrap",
                  maxWidth: "80px",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                }}
              >
                {templateMode}
              </div>
            </Stack>
            <Stack direction="row" spacing={2} alignItems="center">
              <Button
                variant="contained"
                onClick={() => navigate(-1)}
                disabled={isSaving}
                sx={{ bgcolor: "gray", "&:hover": { bgcolor: "darkgray" } }}
              >
                Cancel
              </Button>
              <Button
                variant="contained"
                startIcon={
                  isSaving ? (
                    <CircularProgress size={20} color="inherit" />
                  ) : (
                    <SaveIcon />
                  )
                }
                onClick={handleSaveDesign}
                disabled={isSaving}
                sx={{ bgcolor: "green", "&:hover": { bgcolor: "darkgreen" } }}
              >
                {isSaving ? "Saving..." : "Save Design"}
              </Button>
              <Button
                variant="contained"
                startIcon={<DownloadIcon />}
                onClick={handleDownloadClick}
                sx={{ bgcolor: "#005bb5", "&:hover": { bgcolor: "#004a99" } }}
              >
                Download
              </Button>
            </Stack>
          </Paper>
          <Canvas
            elements={elements}
            selectedElement={selectedElement}
            setSelectedElement={setSelectedElement}
            updateElement={updateElement}
            canvasRef={canvasRef}
            resolution={resolution}
            canvasDimensions={canvasDimensions()}
            dataBindingValue={dataBindingValue}
            templateMode={templateMode}
          />
        </Box>
        <RightSidebar
          selectedElement={elements.find((el) => el.id === selectedElement)}
          canvasColor={canvasColor}
          dataBindingValue={dataBindingValue}
          updateElement={updateElement}
          onDeleteElement={deleteElement}
          onDuplicate={duplicateElement}
          onBringForward={bringForward}
          onSendBackward={sendBackward}
          internalConfigs={internalConfigs}
          truInkEMMSTemplate={truInkEMMSTemplate}
          templateMode={templateMode}
          rightSideRef={rightSideRef}
        />
        <ToastContainer />
      </Box>
      {showBulkUpdateModel && (
        <TruInkUpdateTagModel
          open={showBulkUpdateModel}
          text="You have updated a template. Do you want to refresh all the tags associated with this template as well."
          onSubmit={async () => {
            await callPiggybackAPI(
              "brush/image",
              {},
              {
                siteId: selectedSite.id,
                tenantId: selectedSite.tenantId,
                modifiedValue: "template",
                templateId: templateId,
              },
              "POST",
            );
            navigate(-1);
            setShowBulkUpdateModel(false);
          }}
          onCancel={() => {
            navigate(-1);
            setShowBulkUpdateModel(false);
          }}
          buttonText1={"No"}
          buttonText2={"Yes"}
        />
      )}
    </ThemeProvider>
  );
}
